// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage';
import { invoke } from '@tauri-apps/api/core';
import { RUNTIME, type Runtime } from './platform.js';

// ─────────────────────────────────────────────────────────────────────────────
// Secure storage abstraction
//
// The derived ledger key and the JWT are the crown jewels. Where they live
// depends entirely on the host runtime, so every caller talks to ONE interface
// (`SecureStorageAdapter`) and the correct backing store is chosen once, here,
// by runtime — never by scattered platform checks in feature code.
//
//   Platform / runtime      Backing store
//   ─────────────────────────────────────────────────────────────────────────
//   Web browser             sessionStorage (per-tab, cleared on close)      ✅ done
//   iOS      (Capacitor)    iOS Keychain via @aparajita/…-secure-storage    ✅ done
//   Android  (Capacitor)    Android Keystore via @aparajita/…-secure-storage✅ done
//   macOS    (Tauri)        macOS Keychain (Tauri)                          ⏳ future
//   Windows  (Tauri)        Windows Credential Manager (Tauri)              ⏳ future
//   Linux    (Tauri)        libsecret / KWallet (Tauri)                     ⏳ future
//
// The three OS-specific Capacitor backends are handled inside the one Capacitor
// plugin (Keychain vs Keystore), and likewise the three OS-specific Tauri
// backends will be handled inside one Tauri adapter — so there are exactly three
// adapters to register: web, capacitor, tauri.
//
// EXTENDING: to add a runtime, (1) teach detectRuntime() in platform.ts to
// recognize it, and (2) register a factory in `ADAPTERS` below that returns a
// SecureStorageAdapter. No calling code changes.
//
// Values must be strings; the ledger key (an ArrayBuffer) is base64-encoded by
// the session layer (lib/session.ts) before it reaches this abstraction.
// ─────────────────────────────────────────────────────────────────────────────

export interface SecureStorageAdapter {
  set(key: string, value: string): Promise<void>;
  get(key: string): Promise<string | null>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}

// The keys this app owns. clear() is scoped to these so we never wipe unrelated
// OS-keychain items belonging to other software.
const KEYS: readonly string[] = [
  'hushold_token',
  'hushold_refresh',
  'hushold_keydata',
  'hushold_username',
  'hushold_syncurl',
];

// The crown-jewel items (DEK + tokens). On iOS/Android we store these with the
// strongest OS access class the plugin exposes (SEC-09): passcode-required,
// device-only (never leaves the device via encrypted backup), and automatically
// deleted if the user removes their device passcode. The plugin does not expose
// a per-read biometric ACL, so the biometric prompt (native/biometric.ts) remains
// a JS-level gate in front of these; this hardens the at-rest guarantee.
const SENSITIVE_KEYS = new Set(['hushold_token', 'hushold_refresh', 'hushold_keydata']);

// ── Capacitor adapter (iOS Keychain / Android Keystore) ──────────────────────
// `@aparajita/capacitor-secure-storage` is the actively-maintained
// Keychain/Keystore store from the same author as our biometric plugin. It
// returns null for missing keys (no throw) and needs no custom Swift/Kotlin.
// (`@capacitor-community/biometric-auth`, the spec's suggestion, only does
// biometric prompts, not storage; `capacitor-secure-storage-plugin` only
// officially supports Capacitor ≤ 6 while we target 7 for the Node-20 host.)
function createCapacitorAdapter(): SecureStorageAdapter {
  return {
    async set(key, value) {
      const access = SENSITIVE_KEYS.has(key)
        ? KeychainAccess.whenPasscodeSetThisDeviceOnly
        : KeychainAccess.whenUnlockedThisDeviceOnly;
      // set(key, data, convertDate?, sync?, access?)
      await SecureStorage.set(key, value, false, false, access);
    },
    async get(key) {
      const value = await SecureStorage.get(key);
      if (value === null || value === undefined) return null;
      // We only ever store strings; coerce defensively.
      return typeof value === 'string' ? value : String(value);
    },
    async remove(key) {
      await SecureStorage.remove(key);
    },
    async clear() {
      for (const key of KEYS) await SecureStorage.remove(key);
    },
  };
}

// ── Web adapter (sessionStorage) ─────────────────────────────────────────────
// A thin async wrapper so browser behavior is byte-for-byte unchanged from
// before this abstraction existed — same medium, same lifetime, only awaited.
// Access can throw (private mode / disabled storage), so each call is guarded.
function createWebAdapter(): SecureStorageAdapter {
  return {
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* storage unavailable */
      }
      return Promise.resolve();
    },
    get(key) {
      try {
        return Promise.resolve(sessionStorage.getItem(key));
      } catch {
        return Promise.resolve(null);
      }
    },
    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* storage unavailable */
      }
      return Promise.resolve();
    },
    clear() {
      for (const key of KEYS) {
        try {
          sessionStorage.removeItem(key);
        } catch {
          /* storage unavailable */
        }
      }
      return Promise.resolve();
    },
  };
}

// ── Tauri adapter (macOS Keychain / Windows Credential Manager / libsecret) ──
// The desktop shell backs each session item with the OS keyring via three Rust
// commands (see src-tauri/src/secure.rs): `secure_get`, `secure_set`,
// `secure_remove`. The Rust `keyring` crate picks the native store per OS
// (Keychain / Credential Manager / Secret Service), so this side is a thin,
// runtime-agnostic bridge — same four-method contract as the other adapters.
//
// `invoke` is imported statically (like the Capacitor plugin above); its module
// only touches Tauri globals when actually called, so it is inert in the web and
// Capacitor bundles where this factory is never selected.
function createTauriAdapter(): SecureStorageAdapter {
  return {
    async set(key, value) {
      await invoke('secure_set', { key, value });
    },
    async get(key) {
      // Rust returns Option<String> -> string | null.
      const value = await invoke<string | null>('secure_get', { key });
      return value ?? null;
    },
    async remove(key) {
      await invoke('secure_remove', { key });
    },
    async clear() {
      // Scope the wipe to our own keys, matching the Capacitor adapter, so we
      // never disturb unrelated OS-keyring entries owned by other software.
      for (const key of KEYS) await invoke('secure_remove', { key });
    },
  };
}

// ── Adapter registry ─────────────────────────────────────────────────────────
// One factory per runtime. Adding a runtime = add an entry (+ detectRuntime()).
const ADAPTERS: Record<Runtime, () => SecureStorageAdapter> = {
  capacitor: createCapacitorAdapter,
  web: createWebAdapter,
  tauri: createTauriAdapter,
};

// Resolve the adapter for the current runtime exactly once at module load.
const adapter: SecureStorageAdapter = ADAPTERS[RUNTIME]();

// Returns the platform-appropriate secure storage adapter (Keychain/Keystore on
// Capacitor, sessionStorage on web, OS keyring on Tauri). Chosen by RUNTIME.
export function getSecureStorage(): SecureStorageAdapter {
  return adapter;
}
